import { create } from "zustand";
import type { User } from "./types";

type AuthState = {
  user: User | null;
  sessionChecked: boolean;
  setUser: (user: User | null) => void;
  markSessionChecked: () => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  sessionChecked: false,
  setUser: (user) => {
    if (user?.tenant?.id) {
      localStorage.setItem("tenantId", user.tenant.id);
    }
    set({ user, sessionChecked: true });
  },
  markSessionChecked: () => set({ sessionChecked: true }),
  clear: () => {
    localStorage.removeItem("tenantId");
    set({ user: null, sessionChecked: true });
  },
}));

export const selectIsAuthenticated = (s: AuthState) => !!s.user;
export const selectRole = (s: AuthState) => s.user?.role ?? "member";

export const ROLE_HIERARCHY = ["member", "manager", "admin", "owner"] as const;

export function hasMinRole(role: string | undefined, min: string) {
  return (
    ROLE_HIERARCHY.indexOf(role as (typeof ROLE_HIERARCHY)[number]) >=
    ROLE_HIERARCHY.indexOf(min as (typeof ROLE_HIERARCHY)[number])
  );
}

/**
 * May this user edit the things they can see?
 *
 * On tenant pages the answer is seniority — a member reads, a manager writes.
 * A customer is on no rung of that ladder (hasMinRole is false for them at
 * every rank), but every row they can see is one the API already scoped to
 * them, so there is nothing for seniority to protect them from. Use this
 * instead of hasMinRole on any page a customer shares; keep hasMinRole where
 * the question really is rank inside the tenant's team.
 */
export function canEditOwn(role: string | undefined, min = "manager") {
  return role === "customer" || hasMinRole(role, min);
}
